import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Camera, Upload, Loader2, AlertCircle } from 'lucide-react';
import { petsService, Pet } from '../../lib/petsService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

interface EditPetModalProps {
  isOpen: boolean;
  onClose: () => void;
  pet: Pet | null;
  onSuccess: () => void;
}

export function EditPetModal({ isOpen, onClose, pet, onSuccess }: EditPetModalProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<Pet>>(pet || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(pet?.image_url || null);

  const speciesOptions = [
    { value: 'dog', label: 'Dog' },
    { value: 'cat', label: 'Cat' },
    { value: 'bird', label: 'Bird' },
    { value: 'fish', label: 'Fish' },
    { value: 'rabbit', label: 'Rabbit' },
    { value: 'hamster', label: 'Hamster' },
    { value: 'guinea_pig', label: 'Guinea Pig' },
    { value: 'ferret', label: 'Ferret' },
    { value: 'horse', label: 'Horse' },
    { value: 'reptile', label: 'Reptile' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = useCallback((name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleImageUpload = async (file: File) => {
    if (!file || !user) return;

    setUploading(true);
    setError(null);

    try {
      // Delete old image if it exists
      if (formData.image_url) {
        try {
          const oldPath = formData.image_url.split('/').pop();
          if (oldPath) {
            await supabase.storage
              .from('pet-photos')
              .remove([`${user.id}/${oldPath}`]);
          }
        } catch (deleteError) {
          console.warn('Could not delete old image:', deleteError);
        }
      }

      // Upload new image
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('pet-photos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('pet-photos')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, image_url: publicUrl }));
      setImagePreview(publicUrl);
    } catch (err) {
      console.error('Image upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!pet || !user) return;

    // Validation
    if (!formData.name?.trim()) {
      setError('Pet name is required');
      return;
    }

    if (!formData.species) {
      setError('Species is required');
      return;
    }

    setLoading(true);

    try {
      const updatedPet = await petsService.updatePet(pet.id, {
        ...formData,
        user_id: user.id
      });

      if (updatedPet) {
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error('Update pet error:', err);
      setError(err instanceof Error ? err.message : 'Failed to update pet');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  if (!isOpen || !pet) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Pet</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle size={20} className="text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload Section */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Pet Photo
              </Label>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt={pet.name}
                        className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                      />
                      {uploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                          <Loader2 size={20} className="animate-spin text-white" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <Camera size={24} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="pet-image-upload"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="pet-image-upload"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload size={16} className="mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Photo'}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    JPG, PNG, GIF up to 5MB
                  </p>
                </div>
              </div>
            </div>

            {/* Name and Species */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter pet name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="species" className="text-sm font-medium text-gray-700">
                  Species *
                </Label>
                <Select
                  value={formData.species || ''}
                  onValueChange={(value) => handleInputChange('species', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select species" />
                  </SelectTrigger>
                  <SelectContent>
                    {speciesOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Breed and Birth Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="breed" className="text-sm font-medium text-gray-700">
                  Breed
                </Label>
                <Input
                  id="breed"
                  value={formData.breed || ''}
                  onChange={(e) => handleInputChange('breed', e.target.value)}
                  placeholder="Enter breed"
                />
              </div>
              <div>
                <Label htmlFor="birth_date" className="text-sm font-medium text-gray-700">
                  Birth Date
                </Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={formData.birth_date || ''}
                  onChange={(e) => handleInputChange('birth_date', e.target.value)}
                />
              </div>
            </div>

            {/* Weight and Color */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="weight" className="text-sm font-medium text-gray-700">
                  Weight (kg)
                </Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  value={formData.weight || ''}
                  onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || null)}
                  placeholder="Enter weight"
                />
              </div>
              <div>
                <Label htmlFor="color" className="text-sm font-medium text-gray-700">
                  Color
                </Label>
                <Input
                  id="color"
                  value={formData.color || ''}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  placeholder="Enter color"
                />
              </div>
            </div>

            {/* Microchip and Registration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="microchip_number" className="text-sm font-medium text-gray-700">
                  Microchip Number
                </Label>
                <Input
                  id="microchip_number"
                  value={formData.microchip_number || ''}
                  onChange={(e) => handleInputChange('microchip_number', e.target.value)}
                  placeholder="Enter microchip number"
                />
              </div>
              <div>
                <Label htmlFor="registration_number" className="text-sm font-medium text-gray-700">
                  Registration Number
                </Label>
                <Input
                  id="registration_number"
                  value={formData.registration_number || ''}
                  onChange={(e) => handleInputChange('registration_number', e.target.value)}
                  placeholder="Enter registration number"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes" className="text-sm font-medium text-gray-700">
                Notes
              </Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Enter any additional notes about your pet"
                rows={4}
              />
            </div>

            {/* Guardian Information Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pet Guardian Information</h3>
              <p className="text-sm text-gray-600 mb-4">
                Designate someone to care for your pet if something happens to you.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="guardian_name" className="text-sm font-medium text-gray-700">
                    Guardian Name
                  </Label>
                  <Input
                    id="guardian_name"
                    value={formData.guardian_name || ''}
                    onChange={(e) => handleInputChange('guardian_name', e.target.value)}
                    placeholder="Enter guardian's full name"
                  />
                </div>
                <div>
                  <Label htmlFor="guardian_relationship" className="text-sm font-medium text-gray-700">
                    Relationship
                  </Label>
                  <Input
                    id="guardian_relationship"
                    value={formData.guardian_relationship || ''}
                    onChange={(e) => handleInputChange('guardian_relationship', e.target.value)}
                    placeholder="e.g., Family member, Friend, Neighbor"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="guardian_phone" className="text-sm font-medium text-gray-700">
                    Phone Number
                  </Label>
                  <Input
                    id="guardian_phone"
                    value={formData.guardian_phone || ''}
                    onChange={(e) => handleInputChange('guardian_phone', e.target.value)}
                    placeholder="Enter guardian's phone number"
                  />
                </div>
                <div>
                  <Label htmlFor="guardian_email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </Label>
                  <Input
                    id="guardian_email"
                    type="email"
                    value={formData.guardian_email || ''}
                    onChange={(e) => handleInputChange('guardian_email', e.target.value)}
                    placeholder="Enter guardian's email address"
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="guardian_address" className="text-sm font-medium text-gray-700">
                  Address
                </Label>
                <Textarea
                  id="guardian_address"
                  value={formData.guardian_address || ''}
                  onChange={(e) => handleInputChange('guardian_address', e.target.value)}
                  placeholder="Enter guardian's full address"
                  rows={2}
                />
              </div>

              <div className="mt-4">
                <Label htmlFor="guardian_notes" className="text-sm font-medium text-gray-700">
                  Guardian Notes
                </Label>
                <Textarea
                  id="guardian_notes"
                  value={formData.guardian_notes || ''}
                  onChange={(e) => handleInputChange('guardian_notes', e.target.value)}
                  placeholder="Any special instructions or notes for the guardian"
                  rows={3}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || uploading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
} 